import React, { useState, useEffect, useCallback, useRef } from 'react';
import AmountInput from '../shared/AmountInput';
import useTransactions from '../../hooks/useTransactions';
import { accountsAPI, transactionAPI } from '../../services/api';
import { listCategories } from '../../services/categories';

const ACCOUNT_COLORS = ['#7C3AED', '#2563EB', '#059669', '#EA580C', '#DB2777', '#0891B2'];

const fmt = (amount) => {
  const abs = Math.abs(amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${abs} €`;
};

const fmtDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });

const MONTHS = [
  { v: '', l: 'Tous les mois' }, { v: '01', l: 'Janvier' }, { v: '02', l: 'Février' },
  { v: '03', l: 'Mars' }, { v: '04', l: 'Avril' }, { v: '05', l: 'Mai' }, { v: '06', l: 'Juin' },
  { v: '07', l: 'Juillet' }, { v: '08', l: 'Août' }, { v: '09', l: 'Septembre' },
  { v: '10', l: 'Octobre' }, { v: '11', l: 'Novembre' }, { v: '12', l: 'Décembre' },
];

const Modal = ({ title, onClose, onSubmit, children, width = 480 }) => {
  const submitRef = useRef(onSubmit);
  useEffect(() => { submitRef.current = onSubmit; });
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'Enter' && submitRef.current) {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag !== 'textarea' && tag !== 'select' && tag !== 'button') {
          e.preventDefault();
          submitRef.current();
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-panel" style={{ maxWidth: width }}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

const NewTransactionModal = ({ accountId, accountLabel, categories, onClose, onSave }) => {
  const [type, setType] = useState('expense');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const filteredCats = categories.filter(c => c.type === type);

  const handleSave = async () => {
    if (saving) return;
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setError('Montant invalide.'); return; }
    if (!categoryId) { setError('Veuillez choisir une catégorie.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave({ accountId, categoryId, amount: Math.abs(Number(amount)), date, description, type });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de la création.');
      setSaving(false);
    }
  };

  return (
    <Modal title="Nouvelle transaction" onClose={onClose} onSubmit={handleSave} width={520}>
      <div className="type-toggle">
        <button className={`type-btn expense${type === 'expense' ? ' active' : ''}`} onClick={() => { setType('expense'); setCategoryId(null); }}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 19V5M5 12l7 7 7-7"/></svg>
          Dépense
        </button>
        <button className={`type-btn income${type === 'income' ? ' active' : ''}`} onClick={() => { setType('income'); setCategoryId(null); }}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M19 12l-7-7-7 7"/></svg>
          Revenu
        </button>
      </div>

      <div className="modal-fields">
        <div className="field-row">
          <div className="field-group">
            <label className="field-label">Montant (€)</label>
            <AmountInput value={amount} onChange={setAmount} disabled={saving} style={{ width: '95%' }} />
          </div>
          <div className="field-group">
            <label className="field-label">Date</label>
            <input className="field-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">
            Catégorie
            {filteredCats.length === 0 && <span className="field-opt"> — aucune catégorie pour ce type</span>}
          </label>
          {filteredCats.length > 0 ? (
            <div className="cat-grid">
              {filteredCats.map(cat => (
                <button key={cat._id} className={`cat-grid-btn${categoryId === cat._id ? ' selected' : ''}`}
                  style={{ '--cc': cat.color || '#7C3AED' }} onClick={() => setCategoryId(cat._id)}>
                  <span className="cgb-dot" style={{ background: cat.color || '#7C3AED' }} />
                  <span className="cgb-name">{cat.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ padding: '10px 0', color: 'var(--text3)', fontSize: 13 }}>
              Créez d'abord des catégories de type « {type === 'expense' ? 'Dépense' : 'Revenu'} » dans le tableau de bord.
            </div>
          )}
        </div>

        <div className="field-group">
          <div className="field-label-row">
            <label className="field-label">Description <span className="field-opt">optionnel</span></label>
            <span className="char-counter">{description.length}/200</span>
          </div>
          <input type="text" className="field-input" placeholder="ex : Courses du week-end"
            value={description} onChange={e => setDescription(e.target.value)} maxLength={200} />
        </div>
      </div>

      {error && <div className="modal-error">{error}</div>}
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose} disabled={saving}>Annuler</button>
        <button className="btn-primary" onClick={handleSave} disabled={saving || filteredCats.length === 0}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </Modal>
  );
};

const EditTransactionModal = ({ tx, accountLabel, categories, onClose, onSave, onDelete }) => {
  const [type, setType] = useState(tx.type || 'expense');
  const [amount, setAmount] = useState(String(Math.abs(tx.amount || 0)));
  const [categoryId, setCategoryId] = useState(tx.categoryId || null);
  const [date, setDate] = useState((tx.date || '').split('T')[0] || new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState(tx.description || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const filteredCats = categories.filter(c => c.type === type);

  const handleTypeChange = (t) => { setType(t); setCategoryId(null); };

  const handleSave = async () => {
    if (saving) return;
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) { setError('Montant invalide.'); return; }
    if (!categoryId) { setError('Veuillez choisir une catégorie.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(tx._id, { type, categoryId, amount: Math.abs(Number(amount)), date, description });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de la modification.');
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    setError('');
    try {
      await onDelete(tx._id);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Erreur lors de la suppression.');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Modal title="Modifier la transaction" onClose={onClose} onSubmit={handleSave} width={520}>
      <div className="type-toggle">
        <button className={`type-btn expense${type === 'expense' ? ' active' : ''}`} onClick={() => handleTypeChange('expense')}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 19V5M5 12l7 7 7-7"/></svg>
          Dépense
        </button>
        <button className={`type-btn income${type === 'income' ? ' active' : ''}`} onClick={() => handleTypeChange('income')}>
          <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M19 12l-7-7-7 7"/></svg>
          Revenu
        </button>
      </div>

      <div className="modal-fields">
        <div className="field-row">
          <div className="field-group">
            <label className="field-label">Montant (€)</label>
            <AmountInput value={amount} onChange={setAmount} disabled={saving} style={{ width: '95%' }} />
          </div>
          <div className="field-group">
            <label className="field-label">Date</label>
            <input className="field-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">
            Catégorie
            {filteredCats.length === 0 && <span className="field-opt"> — aucune catégorie pour ce type</span>}
          </label>
          {filteredCats.length > 0 ? (
            <div className="cat-grid">
              {filteredCats.map(cat => (
                <button key={cat._id} className={`cat-grid-btn${categoryId === cat._id ? ' selected' : ''}`}
                  style={{ '--cc': cat.color || '#7C3AED' }} onClick={() => setCategoryId(cat._id)}>
                  <span className="cgb-dot" style={{ background: cat.color || '#7C3AED' }} />
                  <span className="cgb-name">{cat.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ padding: '10px 0', color: 'var(--text3)', fontSize: 13 }}>
              Aucune catégorie disponible pour ce type.
            </div>
          )}
        </div>

        <div className="field-group">
          <div className="field-label-row">
            <label className="field-label">Description <span className="field-opt">optionnel</span></label>
            <span className="char-counter">{description.length}/200</span>
          </div>
          <input type="text" className="field-input" placeholder="ex : Courses du week-end"
            value={description} onChange={e => setDescription(e.target.value)} maxLength={200} />
        </div>
      </div>

      {error && <div className="modal-error">{error}</div>}
      <div className="modal-actions">
        <button className="btn-danger" style={{ marginRight: 'auto' }} onClick={handleDelete} disabled={saving || deleting}>
          {deleting ? 'Suppression...' : confirmDelete ? 'Confirmer ?' : 'Supprimer'}
        </button>
        <button className="btn-ghost" onClick={confirmDelete ? () => setConfirmDelete(false) : onClose} disabled={saving || deleting}>
          {confirmDelete ? 'Garder' : 'Annuler'}
        </button>
        <button className="btn-primary" onClick={handleSave} disabled={saving || deleting || filteredCats.length === 0}>
          {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
        </button>
      </div>
    </Modal>
  );
};

const TransactionsPage = () => {
  const { transactions, loading, fetchTransactions } = useTransactions();
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterMonth, setFilterMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showNewTx, setShowNewTx] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [accountsLoading, setAccountsLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

  useEffect(() => {
    const load = async () => {
      setAccountsLoading(true);
      try {
        const res = await accountsAPI.list();
        const accs = res?.data?.data || [];
        setAccounts(accs);
        const defaultAcc = accs.find(a => a.isDefault) || accs[0];
        if (defaultAcc) {
          setSelectedAccountId(defaultAcc._id);
          const [exp, inc] = await Promise.all([
            listCategories({ type: 'expense', accountId: defaultAcc._id }),
            listCategories({ type: 'income', accountId: defaultAcc._id }),
          ]);
          setCategories([...exp, ...inc]);
          loadTransactions(defaultAcc._id);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setAccountsLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTransactions = useCallback((accId, year, month) => {
    const y = year || filterYear;
    const m = month || filterMonth;
    const params = { accountId: accId || selectedAccountId };
    if (y) {
      if (m) {
        const startDate = `${y}-${m}-01`;
        const endDate = new Date(Number(y), Number(m), 0);
        params.startDate = startDate;
        params.endDate = `${y}-${m}-${String(endDate.getDate()).padStart(2, '0')}`;
      } else {
        params.startDate = `${y}-01-01`;
        params.endDate = `${y}-12-31`;
      }
    }
    if (filterType !== 'all') params.type = filterType;
    if (filterCategory !== 'all') params.categoryId = filterCategory;
    fetchTransactions(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, filterYear, filterMonth, filterType, filterCategory, fetchTransactions]);

  const handleAccountChange = async (accId) => {
    if (accId === selectedAccountId) return;
    setSelectedAccountId(accId);
    try {
      const [exp, inc] = await Promise.all([
        listCategories({ type: 'expense', accountId: accId }),
        listCategories({ type: 'income', accountId: accId }),
      ]);
      setCategories([...exp, ...inc]);
    } catch (e) { /* noop */ }
    loadTransactions(accId);
  };

  const applyFilters = (updates = {}) => {
    const y = updates.year ?? filterYear;
    const m = updates.month ?? filterMonth;
    const t = updates.type ?? filterType;
    const c = updates.category ?? filterCategory;
    const params = { accountId: selectedAccountId };
    if (y) {
      if (m) {
        const endDate = new Date(Number(y), Number(m), 0);
        params.startDate = `${y}-${m}-01`;
        params.endDate = `${y}-${m}-${String(endDate.getDate()).padStart(2, '0')}`;
      } else {
        params.startDate = `${y}-01-01`;
        params.endDate = `${y}-12-31`;
      }
    }
    if (t !== 'all') params.type = t;
    if (c !== 'all') params.categoryId = c;
    fetchTransactions(params);
  };

  const account = accounts.find(a => a._id === selectedAccountId);
  const totalIncome = transactions.filter(t => t.type === 'income').reduce((s, t) => s + (t.amount || 0), 0);
  const totalExpense = transactions.filter(t => t.type === 'expense').reduce((s, t) => s + Math.abs(t.amount || 0), 0);
  const balance = totalIncome - totalExpense;

  const catMap = Object.fromEntries(categories.map(c => [c._id, c]));

  const visibleCategories = filterType === 'all'
    ? categories
    : categories.filter(c => c.type === filterType);

  const badges = [];
  if (filterType !== 'all') badges.push({ key: 'type', label: filterType === 'income' ? 'Revenus' : 'Dépenses' });
  if (filterCategory !== 'all') {
    const cat = catMap[filterCategory];
    if (cat) badges.push({ key: 'category', label: cat.name });
  }

  const clearBadge = (key) => {
    if (key === 'type') { setFilterType('all'); applyFilters({ type: 'all' }); }
    if (key === 'category') { setFilterCategory('all'); applyFilters({ category: 'all' }); }
  };

  const handleSaveTx = async (txData) => {
    await transactionAPI.createTransaction(txData);
    loadTransactions();
  };

  const handleEditTx = async (id, txData) => {
    await transactionAPI.updateTransaction(id, txData);
    loadTransactions();
  };

  const handleDeleteTx = async (id) => {
    await transactionAPI.deleteTransaction(id);
    loadTransactions();
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mes Transactions</h1>
          <p className="page-sub">{account ? `${account.label}` : 'Chargement...'}</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => setShowNewTx(true)} disabled={!selectedAccountId}>
            <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
            Nouvelle transaction
          </button>
        </div>
      </div>

      {/* Account selector cards */}
      {accounts.length > 0 && (
        <div className="acc-selector-grid">
          {accounts.map((a, i) => (
            <div key={a._id}
              className={`acc-selector-card${selectedAccountId === a._id ? ' active' : ''}`}
              onClick={() => handleAccountChange(a._id)}>
              <div className="account-top">
                <span className="account-dot" style={{ background: ACCOUNT_COLORS[i % ACCOUNT_COLORS.length] }} />
                <span className="account-bank">{a.bank || 'Banque'}</span>
                {a.isDefault && <span className="badge-default">Défaut</span>}
              </div>
              <div className="account-name">{a.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon-sm">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
          </div>
          <div className="stat-label">Transactions</div>
          <div className="stat-value">{transactions.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon-sm green">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24"><path d="M12 5v14M19 12l-7-7-7 7"/></svg>
          </div>
          <div className="stat-label">Total gagné</div>
          <div className="stat-value green">+{fmt(totalIncome)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon-sm red">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24"><path d="M12 19V5M5 12l7 7 7-7"/></svg>
          </div>
          <div className="stat-label">Total dépensé</div>
          <div className="stat-value red">−{fmt(totalExpense)}</div>
        </div>
        <div className={`stat-card accent-bg`}>
          <div className="stat-icon-sm">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24"><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4"/></svg>
          </div>
          <div className="stat-label">Solde net</div>
          <div className={`stat-value ${balance >= 0 ? 'green' : 'red'}`}>{balance >= 0 ? '+' : '−'}{fmt(Math.abs(balance))}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <select className="filter-select" value={filterYear} onChange={e => { setFilterYear(e.target.value); applyFilters({ year: e.target.value }); }}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="filter-select" value={filterMonth} onChange={e => { setFilterMonth(e.target.value); applyFilters({ month: e.target.value }); }}>
            {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select>
          <select className="filter-select" value={filterType} onChange={e => {
            const newType = e.target.value;
            setFilterType(newType);
            // Réinitialise la catégorie si elle n'appartient pas au nouveau type
            const currentCat = catMap[filterCategory];
            if (currentCat && newType !== 'all' && currentCat.type !== newType) {
              setFilterCategory('all');
              applyFilters({ type: newType, category: 'all' });
            } else {
              applyFilters({ type: newType });
            }
          }}>
            <option value="all">Tous les types</option>
            <option value="income">Revenus</option>
            <option value="expense">Dépenses</option>
          </select>
          <select className="filter-select" value={filterCategory} onChange={e => { setFilterCategory(e.target.value); applyFilters({ category: e.target.value }); }}>
            <option value="all">Toutes les catégories</option>
            {visibleCategories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>
        {badges.length > 0 && (
          <div className="filter-badges">
            {badges.map(b => (
              <span key={b.key} className="filter-badge">
                {b.label}
                <button onClick={() => clearBadge(b.key)}>
                  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="tx-table-wrap">
        {loading || accountsLoading ? (
          <div className="loading-overlay"><div className="loading-spinner" /></div>
        ) : transactions.length === 0 ? (
          <div className="tx-empty">
            <svg width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.25" viewBox="0 0 24 24" style={{ opacity: 0.3 }}><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
            <p>Aucune transaction pour cette période.</p>
          </div>
        ) : (
          <table className="tx-table">
            <thead>
              <tr>
                <th>Catégorie</th>
                <th style={{ textAlign: 'right' }}>Montant</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(tx => {
                const cat = catMap[tx.categoryId] || catMap[tx.category] || null;
                const catColor = cat?.color || (tx.type === 'income' ? '#22C55E' : '#EF4444');
                return (
                  <tr key={tx._id} className="tx-row" onClick={() => setEditingTx(tx)}>
                    <td>
                      <div className="tx-cat-cell">
                        {cat ? (
                          <span className="tx-cat" style={{ '--cat-color': catColor }}>
                            {cat.name}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                        )}
                        <span className="tx-cat-date">{fmtDate(tx.date)}</span>
                      </div>
                    </td>
                    <td className={`tx-amount ${tx.type === 'income' ? 'income' : 'expense'}`}>
                      {tx.type === 'income' ? '+' : '−'}{fmt(Math.abs(tx.amount))}
                      <span className="tx-amount-desc">{tx.description || tx.label || '—'}</span>
                    </td>
                    <td className="tx-desc">{tx.description || tx.label || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showNewTx && (
        <NewTransactionModal
          accountId={selectedAccountId}
          accountLabel={account?.label}
          categories={categories}
          onClose={() => setShowNewTx(false)}
          onSave={handleSaveTx}
        />
      )}

      {editingTx && (
        <EditTransactionModal
          tx={editingTx}
          accountLabel={account?.label}
          categories={categories}
          onClose={() => setEditingTx(null)}
          onSave={handleEditTx}
          onDelete={handleDeleteTx}
        />
      )}
    </div>
  );
};

export default TransactionsPage;
